import React, { useState, useEffect } from 'react';
import './LastUpdated.css';

/**
 * Reusable LastUpdated component
 *
 * Automatically displays the last modification date of a file.
 * The date is loaded from a metadata file generated at build time.
 *
 * Usage examples:
 *
 * // Using filePath (recommended - automatically gets date from file)
 * <LastUpdated filePath="HomePage.js" />
 *
 * // Using direct date props (fallback or manual dates)
 * <LastUpdated dateTime="2025-12-23" displayDate="December 23, 2025" />
 *
 * // Custom label
 * <LastUpdated filePath="HomePage.js" label="Page last updated:" />
 *
 * @param {Object} props
 * @param {string} props.filePath - Path to the file to get modification date from (e.g., 'HomePage.js' or 'src/pages/HomePage.js')
 * @param {string} props.dateTime - ISO date string (YYYY-MM-DD) - alternative to filePath
 * @param {string} props.displayDate - Formatted date string - alternative to filePath
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Label text (default: "Last updated:")
 */
const LastUpdated = ({
  filePath,
  dateTime,
  displayDate,
  className = '',
  label = 'Last updated:'
}) => {
  const [dateInfo, setDateInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If dateTime and displayDate are provided directly, use them
    if (dateTime && displayDate) {
      setDateInfo({ dateTime, displayDate });
      setLoading(false);
      return;
    }

    // If filePath is provided, try to load from metadata
    if (filePath) {
      loadFileMetadata(filePath);
    } else {
      setLoading(false);
    }
  }, [filePath, dateTime, displayDate]);

  const loadFileMetadata = async (file) => {
    try {
      // Try to load from generated metadata file
      const response = await fetch('/file-metadata.json');
      if (response.ok) {
        const metadata = await response.json();
        // Extract filename without extension as key
        // e.g., "HomePage.js" -> "HomePage", "src/pages/HomePage.js" -> "HomePage"
        const key = file.replace(/^.*\//, '').replace(/\.[^/.]+$/, '');
        if (metadata[key]) {
          setDateInfo(metadata[key]);
          return;
        }
      }
    } catch (error) {
      // Silently fail - metadata file might not exist in development
      // This is expected and not an error
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dateInfo) {
    return null; // Don't render anything if we can't get the date
  }

  return (
    <p className={`update ${className}`.trim()}>
      {label}{' '}
      <time dateTime={dateInfo.dateTime}>{dateInfo.displayDate}</time>
    </p>
  );
};

export default LastUpdated;

